import React, { useState, useMemo } from 'react';
import { Video, User, X, Check, Loader2, RefreshCw, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import PublicVideoPlayer from '../../components/PublicVideoPlayer';
import { useAssignedVideos, useAllUsers } from '../../hooks/queries';
import { userKeys } from '../../hooks/queries/useUsers';
import { videoService } from '../../services/videoService';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export default function AssignedVideos() {
  const [editingReassignment, setEditingReassignment] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState({}); // Track selected user for each video
  const [searchTerm, setSearchTerm] = useState(''); // Search term for filtering users
  const [approvingVideoIndex, setApprovingVideoIndex] = useState(null); // Track which video is being approved
  const queryClient = useQueryClient();

  // Fetch assigned videos
  const { data: assignedVideosData, isLoading: videosLoading, error: videosError, refetch } = useAssignedVideos();
  
  // Fetch all users for reassignment dropdown
  const { data: allUsersData, isLoading: usersLoading, error: usersError } = useAllUsers();
  
  // Extract videos from API response and filter out videos with null public_url
  const assignedVideos = (assignedVideosData?.data || []).filter(video => video.public_url !== null);
  
  // Extract users from API response and format for dropdown
  const availableUsers = useMemo(() => {
    if (!allUsersData || !Array.isArray(allUsersData)) return [];
    
    return allUsersData.map(user => ({
      id: user.id,
      name: user.name || `User #${user.id}`,
      email: user.email || '',
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, [allUsersData]);

  // Filter users based on search term
  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) return availableUsers;
    
    const term = searchTerm.toLowerCase();
    return availableUsers.filter(user => 
      user.name.toLowerCase().includes(term) || 
      user.email.toLowerCase().includes(term)
    );
  }, [availableUsers, searchTerm]);

  // Mutation for reassigning video (using existing assign API)
  const reassignMutation = useMutation({
    mutationFn: ({ filename, newUserId }) => videoService.assignUserToVideo(filename, newUserId),
    onSuccess: () => {
      toast.success('Video reassigned successfully!');
      queryClient.invalidateQueries({ queryKey: ['assigned-videos'] });
      queryClient.invalidateQueries({ queryKey: userKeys.allUsers() }); // Invalidate users cache
      setSelectedUsers({});
      setEditingReassignment(null);
    },
    onError: (error) => {
      console.error('Error reassigning video:', error);
      toast.error('Failed to reassign video');
    }
  });

  // Mutation for approving video
  const approveMutation = useMutation({
    mutationFn: ({ filename }) => {
      console.log('videoService:', videoService);
      console.log('approveVideo method:', videoService.approveVideo);
      console.log('filename:', filename);
      return videoService.approveVideo(filename);
    },
    onSuccess: (data, variables) => {
      toast.success('Video approved successfully!');
      // Remove the approved video from local state by invalidating queries
      queryClient.invalidateQueries({ queryKey: ['assigned-videos'] });
      queryClient.invalidateQueries({ queryKey: userKeys.allUsers() }); // Invalidate users cache
      setApprovingVideoIndex(null); // Clear the approving state
    },
    onError: (error) => {
      console.error('Error approving video:', error);
      toast.error('Failed to approve video');
      setApprovingVideoIndex(null); // Clear the approving state
    }
  });

  // Handle user selection (doesn't call API yet)
  const selectUser = (videoIndex, user) => {
    setSelectedUsers(prev => ({
      ...prev,
      [videoIndex]: user
    }));
    setEditingReassignment(null);
  };

  // Actually reassign the video to the selected user with confirmation
  const reassignVideo = async (videoIndex) => {
    const selectedUser = selectedUsers[videoIndex];
    const video = assignedVideos[videoIndex];
    
    if (!selectedUser || !video) return;

    // Show confirmation dialog
    const isConfirmed = window.confirm(
      `Are you sure you want to reassign this video from "${video.user?.name || 'Unassigned User'}" to "${selectedUser.name}"?`
    );

    if (!isConfirmed) return;

    reassignMutation.mutate({
      filename: video.filename,
      newUserId: selectedUser.id
    });
  };

  // Approve video assignment with confirmation
  const approveVideo = async (videoIndex) => {
    const video = assignedVideos[videoIndex];
    
    if (!video) return;

    // Show confirmation dialog
    const isConfirmed = window.confirm(
      `Are you sure you want to approve this video for "${video.user?.name || 'Unassigned User'}"? This will confirm that the video belongs to this user and remove it from the review list.`
    );

    if (!isConfirmed) return;

    setApprovingVideoIndex(videoIndex); // Set which video is being approved
    approveMutation.mutate({
      filename: video.filename
    });
  };

  // Clear selected user for a video
  const clearSelectedUser = (videoIndex) => {
    setSelectedUsers(prev => {
      const updated = { ...prev };
      delete updated[videoIndex];
      return updated;
    });
  };

  // Close dropdown and clear search
  const closeDropdown = () => {
    setEditingReassignment(null);
    setSearchTerm('');
  };

  if (videosLoading) {
    return (
      <div className="space-y-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-foreground mb-2">Assigned Videos</h2>
          <p className="text-muted-foreground">Loading assigned videos...</p>
        </div>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin h-8 w-8 text-primary" />
        </div>
      </div>
    );
  }

  if (videosError) {
    return (
      <div className="space-y-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-foreground mb-2">Assigned Videos</h2>
          <p className="text-muted-foreground">Error loading assigned videos</p>
        </div>
        <div className="text-center py-12 bg-background border border-secondary-border rounded-xl">
          <div className="text-red-500 mb-4">Failed to load assigned videos</div>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center mx-auto"
          >
            <RefreshCw className="mr-2" size={16} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Assigned Videos</h2>
          <p className="text-muted-foreground">
            Manage video assignments and reassign videos to different users. ({assignedVideos.length} videos)
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-secondary border border-secondary-border text-foreground rounded-lg hover:bg-secondary/90 transition-colors flex items-center"
        >
          <RefreshCw className="mr-2" size={16} />
          Refresh
        </button>
      </div>

      {/* Videos Grid */}
      {assignedVideos.length === 0 ? (
        <div className="text-center py-12 bg-background border border-secondary-border rounded-xl">
          <Video className="mx-auto h-16 w-16 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Assigned Videos</h3>
          <p className="text-muted-foreground mb-4">
            No videos with public URLs are available for review. Videos will appear here once they are assigned to users and have been processed.
          </p>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {assignedVideos.map((video, index) => (
            <div key={video.id} className="bg-background border border-secondary-border rounded-lg overflow-hidden hover:shadow-md transition-all duration-200">
              <div className="aspect-video bg-gray-900 overflow-hidden">
                <PublicVideoPlayer publicUrl={video.public_url} className="w-full h-full" />
              </div>
              <div className="p-4">
                {/* Current Assignment */}
                <div className="mb-4">
                  <h4 className="font-medium text-foreground text-base mb-2">Assign to:</h4>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-secondary border border-secondary-border rounded">
                    <div className="flex items-center space-x-2 flex-1 min-w-0">
                      <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="h-4 w-4 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-foreground text-sm truncate">
                          {video.user?.name || 'Unassigned User'}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {video.user?.email || 'No email available'}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => approveVideo(index)}
                      disabled={approvingVideoIndex === index}
                      className="py-2 px-3 bg-primary hover:bg-primary/90 text-white rounded font-medium transition-colors text-sm disabled:opacity-50 flex items-center justify-center sm:flex-shrink-0 w-full sm:w-auto"
                    >
                      {approvingVideoIndex === index ? (
                        <>
                          <Loader2 className="animate-spin mr-2" size={16} />
                          Approving...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="mr-2" size={16} />
                          Approve
                        </>
                      )}
                    </button>
                  </div>
                </div>
                
                {/* Reassignment Section */}
                <div className="mb-4 p-3 bg-background border border-secondary-border rounded-md">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-foreground text-xs">Reassign Video</span>
                  </div>
                
                {selectedUsers[index] ? (
                  <div className="space-y-3">
                    {/* Selected User Display */}
                    <div className="flex items-center space-x-2 p-2 bg-secondary border border-secondary-border rounded">
                      <div className="w-7 h-7 bg-primary rounded-full flex items-center justify-center">
                        <User className="h-3 w-3 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-foreground text-sm">{selectedUsers[index].name}</div>
                        <div className="text-xs text-muted-foreground truncate max-w-[120px]">{selectedUsers[index].email}</div>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex space-x-2">
                      <button
                        onClick={() => reassignVideo(index)}
                        disabled={reassignMutation.isPending}
                        className="flex-1 py-2 px-3 bg-primary hover:bg-primary/90 text-white rounded font-medium transition-colors text-sm disabled:opacity-50 flex items-center justify-center"
                      >
                        {reassignMutation.isPending ? (
                          <>
                            <Loader2 className="animate-spin mr-1" size={12} />
                            Reassigning...
                          </>
                        ) : (
                          <>
                            <Check className="mr-1" size={12} />
                            Confirm
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setEditingReassignment(index);
                          setSearchTerm('');
                        }}
                        disabled={reassignMutation.isPending}
                        className="px-3 py-2 border border-secondary-border rounded text-muted-foreground hover:bg-secondary transition-colors text-sm"
                      >
                        Change
                      </button>
                      <button
                        onClick={() => clearSelectedUser(index)}
                        disabled={reassignMutation.isPending}
                        className="px-3 py-2 border border-red-200 text-red-500 hover:bg-red-50 rounded transition-colors text-sm"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setEditingReassignment(index);
                      setSearchTerm('');
                    }}
                    className="w-full py-2 px-3 border-2 border-dashed border-secondary-border rounded text-muted-foreground hover:bg-primary hover:text-white hover:border-primary/50 transition-all text-sm"
                  >
                    + Select New User
                  </button>
                )}
                
                {/* Reassignment Dropdown */}
                {editingReassignment === index && (
                  <div className="mt-2 border border-secondary-border rounded bg-secondary shadow-lg z-10">
                    {/* Search Input */}
                    <div className="p-3 border-b border-secondary-border">
                      <input
                        type="text"
                        placeholder="Search users..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-secondary-border rounded focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary bg-background"
                        autoFocus
                      />
                    </div>
                    
                    <div className="max-h-40 overflow-y-auto">
                      <button
                        onClick={() => {
                          clearSelectedUser(index);
                          closeDropdown();
                        }}
                        className="w-full px-3 py-2 text-left hover:bg-primary hover:text-white text-muted-foreground border-b border-gray-100 text-sm"
                      >
                        Cancel selection
                      </button>
                      {usersLoading ? (
                        <div className="px-3 py-2 text-center text-sm text-muted-foreground">
                          Loading users...
                        </div>
                      ) : usersError ? (
                        <div className="px-3 py-2 text-center text-sm text-red-500">
                          Error loading users
                        </div>
                                              ) : (() => {
                          const usersToShow = filteredUsers.filter(user => user.id !== (video.user_id || video.user?.id));
                          
                          if (filteredUsers.length === 0 && availableUsers.length > 0) {
                            return (
                              <div className="px-3 py-2 text-center text-sm text-muted-foreground">
                                No users found matching &quot;{searchTerm}&quot;
                              </div>
                            );
                          } else if (availableUsers.length === 0) {
                            return (
                              <div className="px-3 py-2 text-center text-sm text-muted-foreground">
                                No users available
                              </div>
                            );
                          } else if (usersToShow.length === 0) {
                            return (
                              <div className="px-3 py-2 text-center text-sm text-muted-foreground">
                                No other users available for reassignment
                              </div>
                            );
                          }
                          
                          return usersToShow.map((user) => (
                            <button
                              key={user.id}
                              onClick={() => {
                                selectUser(index, user);
                              }}
                              className="w-full px-3 py-2 text-left hover:bg-primary hover:text-white flex items-center space-x-2 border-b border-gray-100 last:border-b-0"
                            >
                              <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                                <User className="h-3 w-3 text-white" />
                              </div>
                              <div className="hover:text-white">
                                <div className="font-medium text-foreground text-sm">{user.name}</div>
                                <div className="text-xs text-muted-foreground">{user.email}</div>
                              </div>
                            </button>
                          ));
                        })()}
                    </div>
                    <div className="p-2 border-t border-secondary-border bg-secondary hover:bg-primary">
                      <button
                        onClick={closeDropdown}
                        className="w-full py-1 text-xs text-muted-foreground hover:text-white"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                </div>
                
                {/* Video Info */}
                <div className="pt-2 border-t border-secondary-border">
                  <div className="text-xs text-muted-foreground">
                    NameFile: {video.filename.split('/').pop()}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
} 